import { TeamDetailsSupportDto } from '@api/ChallengeClient';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { PageHeader } from '@components/page-header';
import { PageTabs } from '@components/page-tabs';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { observer } from 'mobx-react';
import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useTabs } from '../../components/hooks/use-tabs';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Feed', id: 'feed' },
    { label: 'Members', id: 'members' }
];

interface TeamContextValue {
    refreshTeam(): Promise<TeamDetailsSupportDto | undefined>;
    team: TeamDetailsSupportDto;
}

export const TeamContext = React.createContext<TeamContextValue>(
    {} as TeamContextValue
);

export default observer(() => {
    const { challengeClient } = injectTSDI(Api);
    const location = useLocation();
    const navigate = useNavigate();
    const { teamId } = useParams();
    const { getPageLink, setPageTab, tab } = useTabs(pageTabs);
    const { pathname } = location;

    const teamIdValue = React.useMemo(() => {
        if (!teamId) {
            return undefined;
        }

        const parsedValue = Number(teamId);

        return Number.isNaN(parsedValue) ? undefined : parsedValue;
    }, [teamId]);

    const teamRequest = useWrapRequest<TeamDetailsSupportDto, [number]>(
        async (requestedTeamId) => {
            const response =
                await challengeClient.support.teamSupportControllerGetById(
                    requestedTeamId
                );
            return response.data;
        },
        teamIdValue !== undefined ? { deps: [teamIdValue] } : {}
    );

    React.useEffect(() => {
        setPageTab(pathname, 3);
    }, [pathname, setPageTab]);

    const refreshTeam = React.useCallback(async (): Promise<
        TeamDetailsSupportDto | undefined
    > => {
        if (teamIdValue === undefined) {
            return undefined;
        }

        const nextTeam = (await teamRequest.request([teamIdValue], {
            stateLoading: false,
            throwError: true
        })) as TeamDetailsSupportDto | undefined;

        return nextTeam;
    }, [teamIdValue, teamRequest]);

    const changePage = (page: number) => {
        navigate(getPageLink(page));
    };

    if (teamIdValue === undefined) {
        return (
            <Alert variant="destructive">
                <AlertDescription>Invalid team identifier.</AlertDescription>
            </Alert>
        );
    }

    return teamRequest.match({
        loading: () => (
            <div className="flex min-h-[320px] items-center justify-center">
                <Spinner />
            </div>
        ),
        error: () => (
            <Alert variant="destructive">
                <AlertDescription>
                    Failed to load team details.
                </AlertDescription>
            </Alert>
        ),
        fetched: (team) => (
            <TeamContext.Provider value={{ refreshTeam, team }}>
                <PageHeader
                    title={team.name}
                    description={team.community.name}
                />
                <PageTabs tabs={pageTabs} value={tab} onChange={changePage} />
                <div className="mt-6">
                    <Outlet />
                </div>
            </TeamContext.Provider>
        )
    });
});
